import React from "react";
import { Accordion, AccordionButton, AccordionItem, AccordionPanel, Box, Flex, IconButton, Image, Tag, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { RulesLibrarySuggestion } from "@/api/rulesLibrary";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { colours, qoriaColours } from "@/theme/colours";
import { fonts } from "@/theme/fonts";
import { useSignatureStore } from "@/stores/v2/signatureStore";
import { useClasswizeConfigStore } from "@/stores/classwizeConfigStore";
import { formatWebsiteTarget } from "../utils";
import { AccordionChevron } from "./AccordionChevron";

function suggestionDescriptionKey(
	suggestion: RulesLibrarySuggestion
):
	| "rulesLibrary.quickBlock.allowWebsite"
	| "rulesLibrary.quickBlock.blockWebsite"
	| "rulesLibrary.quickBlock.allowDescription"
	| "rulesLibrary.quickBlock.blockDescription" {
	if (suggestion.type === "URL") {
		return suggestion.action === "ALLOWED" ? "rulesLibrary.quickBlock.allowWebsite" : "rulesLibrary.quickBlock.blockWebsite";
	}
	return suggestion.action === "ALLOWED" ? "rulesLibrary.quickBlock.allowDescription" : "rulesLibrary.quickBlock.blockDescription";
}

interface Props {
	suggestions: RulesLibrarySuggestion[];
	onAdd?: (suggestion: RulesLibrarySuggestion) => void;
	/** Targets already present on the shared Quick Block list. */
	addedTargets?: string[];
	isLoading?: boolean;
	/** When false, the suggestions accordion starts collapsed. */
	defaultExpanded?: boolean;
}

export function QuickBlock(props: Props): JSX.Element | null {
	const { t } = useTranslation();
	const { suggestions, onAdd, addedTargets = [], isLoading = false, defaultExpanded = true } = props;
	const added = new Set(addedTargets);
	const blockingRulesOnly = useClasswizeConfigStore((state) => state.data.create_blocking_rules_only) === true;
	const lookupName = useSignatureStore((state) => state.lookupName);
	const findSignature = useSignatureStore((state) => state.findSignature);
	const getFavicon = useSignatureStore((state) => state.getFavicon);
	const visibleSuggestions = suggestions.filter(
		(suggestion) => !added.has(suggestion.target) && (!blockingRulesOnly || suggestion.action !== "ALLOWED")
	);

	if (visibleSuggestions.length === 0) {
		return null;
	}

	return (
		<Accordion allowToggle defaultIndex={defaultExpanded ? 0 : -1} mb={4}>
			<AccordionItem border="1px dashed" borderColor={qoriaColours.brand.neutral[200]} rounded="xl" bg={qoriaColours.brand.neutral[50]}>
				<AccordionButton px={4} py={4} fontWeight="normal" _hover={{ bg: "transparent" }}>
					<Flex align="center" justify="center" boxSize={8} rounded="lg" bg={qoriaColours.accent.purple[10]} flexShrink={0}>
						<ChakraFAIcon icon="fa-wand-magic-sparkles" variant="solid" color={qoriaColours.accent.purple[600]} boxSize={4} />
					</Flex>
					<Text flex={1} textAlign="left" ml={3} fontWeight={500} color={colours.text.title}>
						{t("rulesLibrary.quickBlock.title")}
					</Text>
					<Tag bg={qoriaColours.brand.neutral[100]} color={colours.text.regular} rounded="full" mr={3}>
						{visibleSuggestions.length}
					</Tag>
					<AccordionChevron />
				</AccordionButton>
				<AccordionPanel px={4} pt={0} pb={4}>
					<Text fontSize="sm" color={colours.text.regular} mb={4}>
						{t("rulesLibrary.quickBlock.subtitle")}
					</Text>
					<Flex direction="column" gap={3}>
						{visibleSuggestions.map((suggestion) => {
							const isWebsite = suggestion.type === "URL";
							const name = isWebsite ? formatWebsiteTarget(suggestion.target) : lookupName(suggestion.target);
							const favicon = !isWebsite && findSignature(suggestion.target) ? getFavicon(suggestion.target) : undefined;
							const descriptionKey = suggestionDescriptionKey(suggestion);
							return (
								<Flex
									key={suggestion.id}
									align="center"
									gap={4}
									bg={qoriaColours.brand.neutral[10]}
									border="1px solid"
									borderColor={qoriaColours.brand.neutral[100]}
									rounded="xl"
									px={4}
									py={3}
								>
									<Flex
										align="center"
										justify="center"
										boxSize={9}
										rounded="xl"
										bg={qoriaColours.brand.neutral[10]}
										border="1px solid"
										borderColor={qoriaColours.brand.neutral[100]}
										flexShrink={0}
										overflow="hidden"
									>
										{favicon ? (
											<Image src={favicon} alt="" boxSize={5} objectFit="contain" />
										) : (
											<ChakraFAIcon icon="fa-shield-halved" variant="solid" color={colours.text.regular} boxSize={4} />
										)}
									</Flex>
									<Box flex={1}>
										<Text fontFamily={fonts.body} fontWeight={500} color={colours.text.title}>
											{name}
										</Text>
										<Text fontSize="sm" color={colours.text.regular}>
											{t(descriptionKey, { label: isWebsite ? name : name })}
										</Text>
									</Box>
									<IconButton
										variant="neutralOutline"
										flexShrink={0}
										aria-label={t("rulesLibrary.quickBlock.addAria", { name })}
										boxSize="32px"
										minW="32px"
										h="32px"
										p={0}
										onClick={() => onAdd?.(suggestion)}
										isDisabled={isLoading}
										icon={<ChakraFAIcon icon="fa-plus" variant="solid" boxSize={4} />}
									/>
								</Flex>
							);
						})}
					</Flex>
				</AccordionPanel>
			</AccordionItem>
		</Accordion>
	);
}
